import { useState } from "react";
import Modal from "./Modal";
import { apiGet } from "src/utils/api";
import { utf8ToBase64 } from "src/utils/utils";
import Loading from "../Loading";
import { DocumentIcon } from "@heroicons/react/24/outline";
import EmailModal from "./EmailModal";
import { useCurrentOrganization, useOrganizationContext } from "src/contexts/OrganizationContext";
import ActionsMenu from "../ActionsMenu";

export interface ViewableItem {
  name: string;
  date: string;
  status: string;
  contentType: string;
  binaryUrl: string;
  content?: string;
  author?: string;
}

export interface EmailContext {
  type: string;
  date: string;
  author: string;
}

export const fetchBinaryContent = async (orgId: number, patientId: string, binaryUrl: string, contentType: string) => {
  const binaryMatch = binaryUrl.match(/Binary\/([^/]+)/);
  if (!binaryMatch) throw new Error('Invalid binary URL format');
  const binaryId = binaryMatch[1];
  const response = await apiGet(`/api/${orgId}/binary/${patientId}/${binaryId}?contentType=${contentType}`);
  return response.json();
};

export const downloadItem = async (orgId: number, patientId: string, item: ViewableItem) => {
  const data = await fetchBinaryContent(orgId, patientId, item.binaryUrl, item.contentType);
  const base64Data = utf8ToBase64(data.data);
  const dataUrl = `data:${item.contentType};base64,${base64Data}`;

  const link = document.createElement('a');
  link.href = dataUrl;
  link.download = `${item.name || 'document'}.${data.extension}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface ViewingItemContentProps {
  item: ViewableItem;
  title: string;
  onBack: () => void;
  onClose: () => void;
  error: string | null;
}

const ViewingItemContent = ({ item, title, onBack, onClose, error }: ViewingItemContentProps) => {
  const isHtml = item.contentType === 'text/html';

  return (
    <Modal title={title} onClose={onClose}>
      <div className="p-6">
        <button onClick={onBack} className="mb-4 text-brand-600 hover:underline">
          &larr; Back
        </button>
        {error && <div className="mb-4 text-red-600">{error}</div>}
        <div className="border rounded-lg p-4 bg-gray-50 max-h-[60vh] overflow-auto">
          {isHtml ? (
            <div dangerouslySetInnerHTML={{ __html: item.content || '' }} />
          ) : (
            <pre className="whitespace-pre-wrap break-words">{item.content}</pre>
          )}
        </div>
      </div>
    </Modal>
  );
};

interface ItemListModalProps {
  title: string;
  items: ViewableItem[];
  loading?: boolean;
  loadingMessage?: string;
  emptyMessage?: string;
  onClose: () => void;
  header?: React.ReactNode;
  getEmailContext?: (item: ViewableItem) => EmailContext;
}

const ItemListModal = ({
  title,
  items,
  loading = false,
  loadingMessage = "Loading...",
  emptyMessage = "No items found",
  onClose,
  header,
  getEmailContext,
}: ItemListModalProps) => {
  const organization = useCurrentOrganization();
  const { patientId } = useOrganizationContext();
  const [ error, setError ] = useState<string | null>(null);
  const [ viewingItem, setViewingItem ] = useState<ViewableItem | null>(null);
  const [ emailItem, setEmailItem ] = useState<ViewableItem | null>(null);

  const handleView = async (item: ViewableItem) => {
    try {
      const data = await fetchBinaryContent(organization.id, patientId || '', item.binaryUrl, item.contentType);
      setViewingItem({ ...item, content: data.data || '' });
    } catch (_) {
      setError('An unknown error occurred');
    }
  };

  const handleDownload = async (item: ViewableItem) => {
    try {
      await downloadItem(organization.id, patientId || '', item);
    } catch (_) {
      setError('An unknown error occurred');
    }
  };

  const handleBack = () => {
    setViewingItem(null);
    setError(null);
  };

  if (viewingItem) {
    return (
      <ViewingItemContent
        item={viewingItem}
        title={title}
        onBack={handleBack}
        onClose={onClose}
        error={error}
      />
    );
  }

  return (
    <Modal title={title} onClose={onClose}>
      {header}
      <ul className="space-y-3">
        {error && <div className="mb-4 text-red-600">{error}</div>}
        {loading ? (
          <div className="flex justify-center mt-8">
            <Loading message={loadingMessage} borderColor="border-brand-600" />
          </div>
        ) : items.length > 0 ? (
          items.map((item, index) => (
            <li key={index} className="border rounded-lg p-3 bg-brand-50 flex items-center justify-between">
              <div className="flex-1">
                <div className="font-semibold text-gray-900">{item.name}</div>
                <div className="text-xs text-gray-600">
                  Date: {item.date && item.date !== 'No Date Available' ? new Date(item.date).toLocaleString() : 'Unknown'}
                </div>
                <div className="text-xs text-gray-600">Status: {item.status}</div>
                <div className="text-xs text-gray-500">Type: {item.contentType}</div>
              </div>
              <div className="ml-4">
                <ActionsMenu
                  actions={[
                    {
                      id: "view-item",
                      label: "View",
                      onClick: () => handleView(item),
                    },
                    {
                      id: "download-item",
                      label: "Download",
                      onClick: () => handleDownload(item),
                    },
                    ...(getEmailContext
                      ? [{
                          id: "email-item",
                          label: "Email",
                          onClick: () => setEmailItem(item),
                        }]
                      : []),
                  ]}
                />
              </div>
            </li>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            <DocumentIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>{emptyMessage}</p>
          </div>
        )}
      </ul>

      {emailItem && getEmailContext && (
        <EmailModal
          context={getEmailContext(emailItem)}
          item={emailItem}
          onClose={() => setEmailItem(null)}
        />
      )}
    </Modal>
  );
};

export default ItemListModal;
